/**
 * Deployment preflight complements browser checks by compiling a minimal WASM module under the active
 * CSP and verifying that the ONNX loader is served with a JavaScript MIME type. It does not fetch the model.
 */
import { remoteLog } from "~/common/logging";

import { describeCapability, detectCapability } from "./capability";
import { logClassifierUnavailable } from "./diagnostics";

// The bare path is used outside a Webpack bundle.
export const ORT_WASM_PATH = process.env.ORT_WASM_PATH ?? "/ort/";
export const ORT_LOADER_FILE = "ort-wasm-simd-threaded.mjs";

export type UnavailableReason = "browser" | "config" | "slow" | "error";

export interface PreflightResult {
  ok: boolean;
  reason?: UnavailableReason;
  /** Diagnostic for the console / Sentry — never rendered to the doctor. */
  detail?: string;
}

/** Valid empty WASM module used to test the active CSP. */
const EMPTY_MODULE = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0]);

/** JavaScript MIME types accepted for module scripts. */
const JS_MIME = new Set([
  "text/javascript",
  "application/javascript",
  "application/ecmascript",
  "text/ecmascript",
  "application/x-ecmascript",
  "application/x-javascript",
  "text/javascript1.0",
  "text/jscript",
  "text/x-javascript",
  "text/x-ecmascript",
]);

function isJsMime(contentType: string | null): boolean {
  if (!contentType) return false;
  return JS_MIME.has(contentType.split(";")[0]?.trim().toLowerCase() ?? "");
}

async function wasmCompiles(): Promise<PreflightResult> {
  try {
    await WebAssembly.instantiate(EMPTY_MODULE);
    return { ok: true };
  } catch (e) {
    // Browser capability has already passed, so failure here is a deployment policy error.
    return { ok: false, reason: "config", detail: `wasm compile blocked: ${String((e as Error)?.message ?? e)}` };
  }
}

/** Prevent a stalled loader probe from blocking both upload interfaces. */
export const PROBE_TIMEOUT_MS = 5_000;

/** Reject after `ms`, and abort the request that is still in flight. */
function deadline(ms: number, controller: AbortController): Promise<PreflightResult> {
  return new Promise((resolve) => {
    setTimeout(() => {
      controller.abort();
      resolve({ ok: false, reason: "config", detail: `probe timed out after ${ms}ms` });
    }, ms);
  });
}

/**
 * Is the onnxruntime loader reachable AND served as a module script?
 *
 * `HEAD` first — headers are the whole question, and this runs on every add-patient page load. Not
 * every host answers HEAD, though, and "this server dislikes HEAD" must never be reported as "the
 * classifier is broken": anything short of a clean, correctly-typed HEAD is re-asked as a one-byte
 * ranged `GET` before a verdict is returned.
 */
async function loaderIsServedCorrectly(): Promise<PreflightResult> {
  const url = `${ORT_WASM_PATH}${ORT_LOADER_FILE}`;
  const controller = new AbortController();

  const ask = async (init: RequestInit): Promise<PreflightResult> => {
    const response = await fetch(url, { credentials: "same-origin", signal: controller.signal, ...init });
    if (!response.ok) {
      return { ok: false, reason: "config", detail: `${url} → HTTP ${response.status}` };
    }
    const contentType = response.headers.get("content-type");
    if (!isJsMime(contentType)) {
      return { ok: false, reason: "config", detail: `${url} served as ${contentType ?? "no content-type"} — a module script needs a JavaScript MIME type` };
    }
    return { ok: true };
  };

  const probe = async (): Promise<PreflightResult> => {
    try {
      const head = await ask({ method: "HEAD" });
      if (head.ok) return head;
      // Second opinion, cheap: one byte, and it exercises the same path the real import will take.
      return await ask({ method: "GET", headers: { Range: "bytes=0-0" } });
    } catch (e) {
      // Network failure, an abort from the deadline below, or a CORS/policy refusal that never
      // reached the server.
      return { ok: false, reason: "config", detail: `${url} unreachable: ${String((e as Error)?.message ?? e)}` };
    }
  };

  // Raced rather than only aborted: a `fetch` that ignores its signal must still not be able to hold
  // the photo block on a placeholder.
  return Promise.race([probe(), deadline(PROBE_TIMEOUT_MS, controller)]);
}

/**
 * Run every check that can be made without downloading the model. Resolves `ok: false` with the
 * reason to show; never rejects — a preflight that throws would be indistinguishable from a browser
 * that cannot run the classifier, and the caller has a UI to render either way.
 */
export async function runPreflight(): Promise<PreflightResult> {
  const capability = detectCapability();
  if (!capability.supported) {
    return { ok: false, reason: "browser", detail: describeCapability(capability) };
  }

  const compiled = await wasmCompiles();
  if (!compiled.ok) return compiled;

  return loaderIsServedCorrectly();
}

/**
 * A `config` verdict is a DEPLOYMENT defect — the doctor is told to contact support, so support has to
 * be told something, once per page load, with the diagnostic the notice does not show. A `browser`
 * verdict is not reported remotely: an old browser is a supported outcome, not a defect.
 */
function report(result: PreflightResult): PreflightResult {
  if (result.ok) return result;
  logClassifierUnavailable(result.reason ?? "config", result.detail);
  if (result.reason === "config") {
    remoteLog(new Error(result.detail ?? "classifier unavailable"), "photo_classifier.preflight");
  }
  return result;
}

let pending: Promise<PreflightResult> | null = null;

/**
 * `runPreflight`, once per page load. The answer is a property of the browser and the deployment, not
 * of the patient — re-probing on every navigation between add/edit/correction would spend a request
 * to learn what is already known.
 */
export function classifierPreflight(): Promise<PreflightResult> {
  pending ??= runPreflight().then(report);
  return pending;
}

/** Clear the cached result before a test that needs a fresh probe. */
export function resetPreflightCache(): void {
  pending = null;
}
